import * as React from "react";
import { Input } from "./input";
import { cn } from "../../lib/utils";

/**
 * NumericInput - A controlled numeric input that allows natural editing
 * 
 * Features:
 * - Allows backspace and normal text editing while typing
 * - Validates and formats only on blur
 * - Supports configurable decimal places (default: 2)
 * - Prevents invalid characters
 */
const NumericInput = React.forwardRef(({
  value,
  onChange,
  onBlur,
  decimals = 2,
  min,
  max,
  allowNegative = false,
  className,
  ...props
}, ref) => {
  // Track the display value separately from the actual value
  const [displayValue, setDisplayValue] = React.useState(
    value !== undefined && value !== null && value !== "" ? String(value) : ""
  );
  const [isFocused, setIsFocused] = React.useState(false);

  // Sync display value when external value changes (and not focused)
  React.useEffect(() => {
    if (!isFocused) {
      setDisplayValue(
        value !== undefined && value !== null && value !== "" ? String(value) : ""
      );
    }
  }, [value, isFocused]);

  const handleChange = (e) => {
    const inputValue = e.target.value;
    
    // Allow empty string
    if (inputValue === "") {
      setDisplayValue("");
      onChange?.(e);
      return;
    }

    // Simple validation: allow digits, one decimal point, and optionally negative sign
    // Be permissive during typing, clean up on blur
    const negativeStart = allowNegative && inputValue.startsWith('-');
    const valueToCheck = negativeStart ? inputValue.slice(1) : inputValue;
    
    // Count decimal points
    const decimalCount = (valueToCheck.match(/\./g) || []).length;
    
    // Check if all characters are valid (digits and at most one decimal)
    const isValidChars = /^[\d.]*$/.test(valueToCheck);
    const hasValidDecimals = decimalCount <= 1;
    
    // If decimals=0, don't allow decimal point at all
    const decimalAllowed = decimals > 0 || !valueToCheck.includes('.');
    
    if (isValidChars && hasValidDecimals && decimalAllowed) {
      setDisplayValue(inputValue);
      onChange?.(e);
    }
  };

  const handleFocus = (e) => {
    setIsFocused(true);
    // Select all text on focus for easy overwriting
    setTimeout(() => e.target.select(), 0);
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    
    let finalValue = displayValue;
    
    // Clean up the value on blur
    if (finalValue === "" || finalValue === "." || finalValue === "-" || finalValue === "-.") {
      finalValue = "";
    } else {
      let numValue = parseFloat(finalValue);
      
      if (isNaN(numValue)) {
        finalValue = "";
      } else {
        // Apply min/max constraints
        if (min !== undefined && numValue < min) numValue = min;
        if (max !== undefined && numValue > max) numValue = max;
        
        // Format to specified decimals
        if (decimals > 0) {
          // Keep meaningful decimals but don't force trailing zeros
          const fixed = numValue.toFixed(decimals);
          finalValue = parseFloat(fixed).toString();
        } else {
          finalValue = String(Math.round(numValue));
        }
          
        // Ensure we don't show "-0"
        if (finalValue === "-0") finalValue = "0";
      }
    }
    
    setDisplayValue(finalValue);
    
    // Create synthetic event with cleaned value
    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        value: finalValue
      }
    };
    
    onBlur?.(syntheticEvent);
  };

  return (
    <Input
      ref={ref}
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      className={cn("tabular-nums", className)}
      {...props}
    />
  );
});

NumericInput.displayName = "NumericInput";

export { NumericInput };
